// src/pages/userinvite.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Login.css';
import useNotificationStore from '../stores/notificationStore';

const API_URL = import.meta.env.VITE_API_URL;

export default function UserInvite() {
  const [phase, setPhase] = useState('checking'); // checking | form | error
  const [errorText, setErrorText] = useState('');
  const { showNotification } = useNotificationStore();
  const navigate = useNavigate();
  const location = useLocation();

  const token = new URLSearchParams(location.search).get('t') || '';

  useEffect(() => {
    if (!token) {
      setPhase('error');
      setErrorText('招待トークンが見つかりません。');
      showNotification('招待トークンが見つかりません。', 'error');
      return;
    }

    const validateToken = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/userinvite/validate/`, { token });
        if (res.data?.valid) {
          setPhase('form');
        } else {
          setPhase('error');
          setErrorText('無効な招待トークンです。');
          showNotification('無効な招待トークンです。', 'error');
        }
      } catch {
        setPhase('error');
        setErrorText('通信エラーが発生しました。');
        showNotification('通信エラーが発生しました。', 'error');
      }
    };

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (phase === 'checking') {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2 className="login-title">招待を確認中...</h2>
          <p className="login-subtitle">しばらくお待ちください。</p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2 className="login-title">招待エラー</h2>
          <div className="error-alert">{errorText}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card" role="dialog" aria-labelledby="invite-title">
        <h2 id="invite-title" className="login-title">ユーザー招待</h2>
        <p className="login-subtitle">新規アカウントを作成して参加できます。</p>
        <InviteSignupForm token={token} />
      </div>
    </div>
  );
}


function InviteSignupForm({ token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotificationStore();

  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    userAddress: '',
    userPass: '',
    userPass2: '',
    userPhone: '',
    postalCode: '',
    prefecture: '',
    cityArea: '',
    addressDetail: '',
  });
  const [showPassword, setShowPassword] = useState({ pass1: false, pass2: false });
  const [submitting, setSubmitting] = useState(false);
  const [postalLoading, setPostalLoading] = useState(false);
  const [postalError, setPostalError] = useState('');
  // StrictMode による useEffect 二重実行を防ぐフラグ
  const submittedRef = useRef(false);

  const updateField = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // ---- 郵便番号入力（数字のみ・最大7桁） ----
  const handlePostalCodeChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 7);
    setFormData((prev) => ({ ...prev, postalCode: raw }));
    setPostalError('');
  };

  // ---- 住所検索ボタン ----
  const handlePostalSearch = async () => {
    const code = formData.postalCode.trim();
    if (code.length !== 7) {
      setPostalError('7桁の郵便番号を入力してください。');
      return;
    }

    setPostalLoading(true);
    setPostalError('');
    try {
      const res = await axios.get(`${API_URL}/api/utility/postal/search/`, {
        params: { zipcode: code },
      });
      const result = res.data?.results?.[0];
      if (result) {
        setFormData((prev) => ({
          ...prev,
          prefecture: result.address1 || '',
          cityArea:   (result.address2 || '') + (result.address3 || ''),
        }));
      } else {
        const msg = res.data?.message || '該当する住所が見つかりませんでした。';
        setPostalError(msg);
        showNotification(msg, 'error');
        setFormData((prev) => ({ ...prev, prefecture: '', cityArea: '' }));
      }
    } catch {
      const msg = '住所の取得に失敗しました。';
      setPostalError(msg);
      showNotification(msg, 'error');
    } finally {
      setPostalLoading(false);
    }
  };

  // ---- OTP検証成功後の処理 ----
  // OTP画面から戻った際、フォームの入力内容を sessionStorage から復元してから登録処理を実行する。
  // 先に formData を復元しておくことで、登録に失敗しても入力内容がリセットされずに残る。
  // submittedRef で React StrictMode による useEffect 二重実行（→ API 二重送信）を防ぐ。
  useEffect(() => {
    const state = location.state;
    if (!state?.otpVerified || !state?.email) return;

    // 二重実行ガード（StrictMode 対策）
    if (submittedRef.current) return;
    submittedRef.current = true;

    const draftRaw = sessionStorage.getItem('signupDraft');
    if (!draftRaw) {
      showNotification('入力内容が失われました。もう一度やり直してください。', 'error');
      return;
    }

    try {
      const draft = JSON.parse(draftRaw);

      // メールアドレスの整合性チェック
      if (draft.userAddress?.trim().toLowerCase() !== state.email.trim().toLowerCase()) {
        showNotification('メールアドレスが一致しません。もう一度やり直してください。', 'error');
        submittedRef.current = false; // チェック失敗時はフラグをリセット
        return;
      }

      // ① まずフォームに入力内容を復元する（登録失敗時もフォームが空にならないようにする）
      setFormData(draft);

      // ② 復元後に登録処理を実行する
      submitRegistration(draft);

    } catch {
      showNotification('入力内容の復元に失敗しました。もう一度やり直してください。', 'error');
      submittedRef.current = false; // 例外時はフラグをリセット
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ---- バリデーション ----
  const validateForm = (data) => {
    if (!data.lastName?.trim())    return '苗字を入力してください。';
    if (!data.firstName?.trim())   return '名前を入力してください。';
    if (!data.userAddress?.trim()) return 'メールアドレスを入力してください。';
    if (!data.userPass || data.userPass.length < 8)
      return 'パスワードは8文字以上にしてください。';
    if (data.userPass !== data.userPass2) return 'パスワード（確認）が一致しません。';
    if (data.userPhone?.trim() && !/^[\d\-+() ]{7,15}$/.test(data.userPhone.trim())) {
      return '電話番号の形式が正しくありません。';
    }
    if (!data.postalCode?.trim())    return '郵便番号を入力してください。';
    if (!data.prefecture?.trim())    return '都道府県を入力してください。';
    if (!data.cityArea?.trim())      return '市区町村・町名を入力してください。';
    if (!data.addressDetail?.trim()) return '番地・建物名を入力してください。';
    return null;
  };

  // ---- 登録処理（OTP成功後に呼ばれる） ----
  const submitRegistration = async (data) => {
    const validationError = validateForm(data);
    if (validationError) {
      showNotification(validationError, 'error');
      return;
    }

    const userName = `${data.lastName.trim()} ${data.firstName.trim()}`;
    // 住所は「都道府県 + 市区町村 + 番地」のみ結合（郵便番号・スペースなし）
    const userAddress2 = [
      data.prefecture,
      data.cityArea,
      data.addressDetail,
    ].map(s => s?.trim()).filter(Boolean).join('');

    try {
      setSubmitting(true);

      const res = await axios.post(`${API_URL}/api/auth/userinvite/accept-member/`, {
        token,
        userName,
        userAddress:  data.userAddress.trim(),
        userPass:     data.userPass,
        userPhone:    data.userPhone?.trim() || '',
        userPostcode: data.postalCode?.trim() || '',  // 郵便番号は独立して送信
        userAddress2,                                  // 住所本体（郵便番号・スペースなし）
      });

      if (res.data?.success) {
        sessionStorage.removeItem('signupDraft');
        sessionStorage.removeItem('otpFlowState');
        localStorage.setItem('lastLoginID', data.userAddress.trim());

        if (res.data?.accessToken) {
          sessionStorage.setItem('accessToken', res.data.accessToken);
        }
        
        navigate(
          res.data.created ? '/auth/notice/userinvite' : '/auth/notice/userinvite-already',
          { replace: true }
        );
      } else {
        showNotification(res.data?.message || '登録に失敗しました。', 'error');
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- フォーム送信 → OTPへ ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm(formData);
    if (validationError) {
      showNotification(validationError, 'error');
      return;
    }

    const email = formData.userAddress.trim();

    try {
      setSubmitting(true);

      const otpRes = await axios.post(`${API_URL}/api/otp/start`, {
        userAddress: email,
        purpose: 'signup',
      });

      const { challengeId, ttlSec, resendCooldownSec, maskedEmail } = otpRes.data || {};
      if (!challengeId) {
        showNotification('ワンタイムパスの開始に失敗しました。', 'error');
        return;
      }

      sessionStorage.setItem('signupDraft', JSON.stringify({ ...formData, userAddress: email }));

      const returnTo = `/userinvite?t=${encodeURIComponent(token)}`;
      const returnState = { otpVerified: true, email };

      sessionStorage.setItem('otpFlowState', JSON.stringify({
        challengeId,
        userAddress: email,
        maskedEmail,
        ttlSec,
        resendCooldownSec,
        returnTo,
        returnState,
      }));

      navigate('/otpverify', {
        state: {
          challengeId,
          userAddress: email,
          maskedEmail,
          ttlSec,
          resendCooldownSec,
          returnTo,
          returnState,
        },
        replace: true,
      });
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApiError = (err) => {
    const status = err?.response?.status;
    const message = err?.response?.data?.message || err?.response?.data?.error;
    const errorMessages = {
      400: message || '入力内容を確認してください。',
      403: 'CSRFエラーが発生しました。ページを再読み込みしてください。',
      409: 'このメールアドレスは既に登録されています。',
      429: '操作が集中しています。しばらくしてからお試しください。',
    };
    showNotification(errorMessages[status] || message || '通信エラーが発生しました。', 'error');
  };

  

  return (
    <form onSubmit={handleSubmit} noValidate>

      {/* ── 氏名（苗字・名前）── */}
      <div className="form-group">
        <label>氏名<span className="field-required">必須</span></label>
        <div className="name-fields">
          <input
            id="lastName"
            type="text"
            value={formData.lastName}
            onChange={updateField('lastName')}
            placeholder="苗字"
            required
            autoComplete="family-name"
            aria-label="苗字"
          />
          <input
            id="firstName"
            type="text"
            value={formData.firstName}
            onChange={updateField('firstName')}
            placeholder="名前"
            required
            autoComplete="given-name"
            aria-label="名前"
          />
        </div>
      </div>

      {/* ── メールアドレス ── */}
      <div className="form-group">
        <label htmlFor="userAddress">メールアドレス<span className="field-required">必須</span></label>
        <input
          id="userAddress"
          type="email"
          value={formData.userAddress}
          onChange={updateField('userAddress')}
          required
          autoComplete="username"
          placeholder="your@example.com"
        />
      </div>

      {/* ── 電話番号（任意） ── */}
      <div className="form-group">
        <label htmlFor="userPhone">
          電話番号
          <span className="field-optional">任意</span>
        </label>
        <input
          id="userPhone"
          type="tel"
          value={formData.userPhone}
          onChange={updateField('userPhone')}
          placeholder="090-0000-0000"
          autoComplete="tel"
        />
      </div>

      {/* ── 郵便番号 ── */}
      <div className="form-group">
        <label htmlFor="postalCode">郵便番号<span className="field-required">必須</span></label>
        <div className="postal-field">
          <span className="postal-prefix">〒</span>
          <input
            id="postalCode"
            type="text"
            inputMode="numeric"
            value={formData.postalCode}
            onChange={handlePostalCodeChange}
            placeholder="1234567"
            maxLength={7}
            autoComplete="postal-code"
          />
          <button
            type="button"
            className="postal-search-btn"
            onClick={handlePostalSearch}
            disabled={postalLoading || formData.postalCode.length !== 7}
            aria-label="住所を検索"
          >
            {postalLoading ? '検索中…' : '住所を検索'}
          </button>
        </div>
        {postalError && <span className="field-error">{postalError}</span>}
        <span className="field-hint">ハイフンなし7桁で入力してください</span>
      </div>

      {/* ── 都道府県 ── */}
      <div className="form-group">
        <label htmlFor="prefecture">都道府県<span className="field-required">必須</span></label>
        <input
          id="prefecture"
          type="text"
          value={formData.prefecture}
          onChange={updateField('prefecture')}
          placeholder="東京都"
          autoComplete="address-level1"
          required
        />
      </div>

      {/* ── 市区町村・町名 ── */}
      <div className="form-group">
        <label htmlFor="cityArea">市区町村・町名<span className="field-required">必須</span></label>
        <input
          id="cityArea"
          type="text"
          value={formData.cityArea}
          onChange={updateField('cityArea')}
          placeholder="渋谷区渋谷"
          autoComplete="address-level2"
          required
        />
      </div>

      {/* ── 番地・建物名 ── */}
      <div className="form-group">
        <label htmlFor="addressDetail">番地・建物名<span className="field-required">必須</span></label>
        <input
          id="addressDetail"
          type="text"
          value={formData.addressDetail}
          onChange={updateField('addressDetail')}
          placeholder="1-2-3 ○○ビル 4F"
          autoComplete="address-line2"
          required
        />
      </div>

      {/* ── パスワード ── */}
      <div className="form-group">
        <label htmlFor="userPass">パスワード<span className="field-required">必須</span></label>
        <div className="password-field">
          <input
            id="userPass"
            type={showPassword.pass1 ? 'text' : 'password'}
            value={formData.userPass}
            onChange={updateField('userPass')}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword((prev) => ({ ...prev, pass1: !prev.pass1 }))}
            aria-label={showPassword.pass1 ? 'パスワードを非表示' : 'パスワードを表示'}
          >
            {showPassword.pass1 ? '隠す' : '表示'}
          </button>
        </div>
      </div>

      {/* ── パスワード（確認） ── */}
      <div className="form-group">
        <label htmlFor="userPass2">パスワード（確認）<span className="field-required">必須</span></label>
        <div className="password-field">
          <input
            id="userPass2"
            type={showPassword.pass2 ? 'text' : 'password'}
            value={formData.userPass2}
            onChange={updateField('userPass2')}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword((prev) => ({ ...prev, pass2: !prev.pass2 }))}
            aria-label={showPassword.pass2 ? 'パスワードを非表示' : 'パスワードを表示'}
          >
            {showPassword.pass2 ? '隠す' : '表示'}
          </button>
        </div>
      </div>
      
      <button
        type="submit"
        className={`login-button ${submitting ? 'is-loading' : ''}`}
        disabled={submitting}
      >
        {submitting ? '登録中…' : '登録して参加'}
      </button>
    </form>
  );
}